import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin, Mail, Linkedin, ChevronDown, Download } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { blogPosts } from '../data/blogPosts';

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const StatPill = ({ value, label }) => (
  <div className="flex flex-col items-center px-6 py-4 border border-gray-800 rounded-2xl bg-gray-900/50 min-w-[100px]">
    <span className="text-2xl md:text-3xl font-black text-orange-500 tracking-tight">{value}</span>
    <span className="text-[10px] uppercase tracking-widest text-gray-500 mt-1 text-center leading-tight">{label}</span>
  </div>
);

const ImpactStat = ({ value, label }) => (
  <motion.div
    variants={fadeUp}
    className="flex flex-col items-center p-6 bg-gray-900 border border-gray-800 rounded-2xl text-center"
  >
    <span className="text-4xl md:text-5xl font-black text-orange-500 tracking-tight mb-2">{value}</span>
    <span className="text-xs text-gray-400 leading-relaxed max-w-[140px]">{label}</span>
  </motion.div>
);

const PortfolioCard = ({ tag, tagColor, title, description, stack, status, statusColor }) => (
  <motion.div
    variants={fadeUp}
    className="group p-6 md:p-8 bg-gray-900 border border-gray-800 rounded-3xl hover:border-orange-500/40 transition-all duration-300 hover:bg-gray-900/80 flex flex-col"
  >
    <div className="flex items-start justify-between mb-4">
      <span className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full ${tagColor}`}>
        {tag}
      </span>
      {status && (
        <span className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full ${statusColor}`}>
          {status}
        </span>
      )}
    </div>
    <h3 className="text-lg md:text-xl font-black text-white mb-3 group-hover:text-orange-400 transition-colors">
      {title}
    </h3>
    <p className="text-gray-400 text-sm leading-relaxed flex-1 mb-5">{description}</p>
    <div className="flex flex-wrap gap-2">
      {stack.map((t) => (
        <span key={t} className="text-[10px] text-gray-500 border border-gray-700 px-2 py-1 rounded-md font-mono">
          {t}
        </span>
      ))}
    </div>
  </motion.div>
);

const BlogCard = ({ post }) => (
  <motion.div variants={fadeUp}>
    <Link
      to={`/meet-Joseph/blog/${post.slug}`}
      className="group block p-6 md:p-8 bg-gray-900 border border-gray-800 rounded-3xl hover:border-orange-500/40 transition-all duration-300 h-full"
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-[10px] text-gray-500 font-mono">{post.date}</span>
        <span className="w-px h-3 bg-gray-700" />
        <span className="text-[10px] text-gray-500 font-mono">{post.readTime}</span>
      </div>
      <h3 className="text-lg md:text-xl font-black text-white mb-1 group-hover:text-orange-400 transition-colors">
        {post.title}
      </h3>
      <p className="text-sm text-orange-500/70 mb-3 font-medium">{post.subtitle}</p>
      <p className="text-gray-400 text-sm leading-relaxed mb-5">{post.excerpt}</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {post.tags.map((tag) => (
          <span key={tag} className="text-[10px] text-orange-500/70 border border-orange-500/20 px-2 py-1 rounded-md">
            {tag}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1.5 text-orange-500 text-sm font-bold group-hover:gap-3 transition-all">
        Read post <ArrowUpRight className="w-4 h-4" />
      </div>
    </Link>
  </motion.div>
);

const SkillGroup = ({ category, skills }) => (
  <div className="p-5 bg-gray-900/60 border border-gray-800 rounded-2xl">
    <p className="text-[10px] uppercase tracking-widest text-orange-500 font-bold mb-4">{category}</p>
    <div className="flex flex-wrap gap-2">
      {skills.map((s) => (
        <span key={s} className="text-xs text-gray-300 border border-gray-700 bg-gray-950 px-3 py-1.5 rounded-lg font-mono">
          {s}
        </span>
      ))}
    </div>
  </div>
);

export default function MeetJoseph() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 overflow-x-hidden">

      <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} onNavClick={() => {}} activeSection="" />

      {/* ── HERO ── */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 px-6">
        {/* Subtle radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(249,115,22,0.06) 0%, transparent 70%)',
          }}
        />

        <div className="container mx-auto max-w-5xl relative grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 lg:gap-10 items-start">
          <div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-6">
                <span className="w-6 h-px bg-orange-500" />
                Founder · Senior Software Engineer
                <span className="w-6 h-px bg-orange-500" />
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="text-6xl md:text-8xl lg:text-9xl font-black text-white tracking-tighter leading-none mb-6"
            >
              Joseph<br />
              <span className="text-orange-500">Macharia.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed mb-10"
            >
              12 years. Three industries. One consistent outcome:{' '}
              <span className="text-white font-medium">systems that hold under real load.</span>
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="flex flex-wrap gap-3 mb-12"
            >
              <StatPill value="12" label="Years" />
              <StatPill value="3" label="Industries" />
              <StatPill value="5" label="Countries" />
              <StatPill value="10k+" label="Txn / Day" />
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
              className="flex flex-wrap gap-4"
            >
              <a
                href="#story"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-black text-sm uppercase tracking-wider rounded-full transition-colors"
              >
                Read my story <ChevronDown className="w-4 h-4" />
              </a>
              <a
                href="https://linkedin.com/in/j-macharia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-gray-700 hover:border-orange-500 text-gray-300 hover:text-white font-bold text-sm uppercase tracking-wider rounded-full transition-all"
              >
                LinkedIn <ArrowUpRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>

          {/* Portrait */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="relative order-first lg:order-last mx-auto lg:mx-0 w-full max-w-[320px] lg:max-w-none"
          >
            {/* glow behind photo, echoes hero glow above */}
            <div
              className="absolute -inset-6 rounded-[2rem] pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 30%, rgba(249,115,22,0.15) 0%, transparent 70%)' }}
            />
            <div className="relative rounded-3xl overflow-hidden border border-gray-800 bg-gray-900">
              <img
                src="/images/joseph-macharia.png"
                alt="Joseph Macharia"
                className="w-full h-auto object-cover grayscale-[15%]"
              />
              {/* fade into page bg so photo edge doesn't look pasted */}
              <div
                className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                style={{ background: 'linear-gradient(to bottom, transparent, rgba(3,7,18,0.7))' }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STORY ── */}
      <section id="story" className="py-20 md:py-28 px-6 border-t border-gray-800/50">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-10 lg:gap-20">

            {/* Sidebar label */}
            <div>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                <p className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">01</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">The Engineer</p>
              </motion.div>
            </div>

            {/* Content */}
            <div>
              {/* Pull quote */}
              <motion.blockquote
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="text-2xl md:text-4xl font-black text-white leading-tight mb-12 border-l-4 border-orange-500 pl-6"
              >
                "Getting it right means nobody notices it's running."
              </motion.blockquote>

              <motion.div
                className="space-y-5 text-gray-400 text-base leading-relaxed mb-12"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.1 } } }}
              >
                {[
                  'My career started at Navisat Telematics, where I spent six years building cargo tracking and fleet monitoring infrastructure across the COMESA region. I learned hardware-to-software integration from the ground up — reading CAN bus data off vehicles to stream real-time telemetry: speed, fuel levels, tyre pressure, engine metrics.',
                  'Beyond the code, I specialise in building the technical culture — the architecture, the testing standards, and the communication patterns — that allow teams to ship complex systems without the chaos. A senior engineer writes code. I also make the engineers around me faster.',
                  'I rebuilt their cargo alerts engine from a hardcoded system into a fully configurable rules engine. Operators could define their own geofencing boundaries, tampering triggers, and delay thresholds without a single code change. The project I\'m most proud of from that period: an OTA firmware update framework with a dual-partition fail-safe. Fleet-wide deployments went from months to hours. Physical device recalls: eliminated.',
                  'At Kopo, a P2P lending platform, I architected a Bayesian matching engine from scratch that paired borrowers and lenders by balancing collateral types, interest rates, and risk profiles simultaneously. The hard problem was not the matching logic — it was making double-allocation physically impossible when two lenders hit the same borrower at the same millisecond.',
                  'At Jumia, I joined an order fulfilment platform processing 10,000+ transactions per day across five countries. I came in on the legacy PHP/Zend 1 codebase, where I designed a Transactional Outbox Pattern from scratch — no library existed for that stack, so I built one. As the platform modernised I transitioned into Java, building the Kafka publishing and consumer services on a hexagonal architecture. It was there I traced and fixed silent message drops — caused by the secrets vault getting overwhelmed under concurrency. No errors. No alerts. Just orders quietly not propagating. I was promoted to on-call engineer within a year.',
                ].map((para, i) => (
                  <motion.p key={i} variants={fadeUp}>
                    {para}
                  </motion.p>
                ))}
              </motion.div>

              {/* Impact stats */}
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.1 } } }}
                className="grid grid-cols-2 md:grid-cols-4 gap-3"
              >
                <ImpactStat value="−40%" label="Alert fatigue after rebuilding rules engine" />
                <ImpactStat value="90%" label="Match success rate on P2P lending engine" />
                <ImpactStat value="−60%" label="Cross-system sync failures after Kafka fix" />
                <ImpactStat value="+30%" label="OCR confidence before reconciliation" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT I'M BUILDING ── */}
      <section className="py-20 md:py-28 px-6 border-t border-gray-800/50">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-10 lg:gap-20">

            <div>
              <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">02</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">Currently Shipping</p>
              </motion.div>
            </div>

            <div>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-black text-white mb-8"
              >
                What I'm building
              </motion.h2>

              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.12 } } }}
                className="grid grid-cols-1 md:grid-cols-2 gap-5"
              >
                <PortfolioCard
                  tag="Fintech · Multi-tenant"
                  tagColor="bg-cyan-500/10 text-cyan-400"
                  status="Live"
                  statusColor="bg-green-500/10 text-green-400"
                  title="Roundcircle"
                  description="Enterprise-grade financial infrastructure for Kenyan Chamas — informal investment groups that pool money and make collective financial decisions. A three-layer double-entry GL engine where operation codes map dynamically to org-level accounting rules, a Transactional Outbox for guaranteed loan disbursements, and a Coverage Horizon algorithm that prevents lopsided payments."
                  stack={['Node.js', 'MySQL', 'RabbitMQ', 'Redis']}
                />
                <PortfolioCard
                  tag="SaaS · Billing"
                  tagColor="bg-purple-500/10 text-purple-400"
                  status="Live"
                  statusColor="bg-green-500/10 text-green-400"
                  title="TrueCost"
                  description="Billing and subscription infrastructure that grows with your product without requiring a developer every time a plan changes. Plans self-describe their features via JSONB — the frontend renders UI components from that schema with zero code changes per new feature. Webhook security uses AES-256-GCM with HMAC signing."
                  stack={['NestJS', 'TypeScript', 'PostgreSQL', 'React', 'AWS']}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PAST WORK ── */}
      <section className="py-20 md:py-28 px-6 border-t border-gray-800/50">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-10 lg:gap-20">

            <div>
              <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">03</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">Work History</p>
              </motion.div>
            </div>

            <div>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-black text-white mb-8"
              >
                Where I've worked
              </motion.h2>

              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.1 } } }}
                className="grid grid-cols-1 md:grid-cols-2 gap-5"
              >
                <PortfolioCard
                  tag="E-commerce · Scale"
                  tagColor="bg-orange-500/10 text-orange-400"
                  title="Jumia — Fulfilment Platform"
                  description="Designed a Transactional Outbox Pattern from scratch on a legacy PHP/Zend 1 codebase, then transitioned to Java to build the Kafka publishing and consumer services on a hexagonal architecture. Traced and fixed silent message drops causing orders to disappear silently across five countries. Promoted to on-call engineer within one year."
                  stack={['Java', 'Hexagonal', 'Kafka', 'PHP (Zend)', 'MySQL']}
                />
                <PortfolioCard
                  tag="Fintech · P2P Lending"
                  tagColor="bg-cyan-500/10 text-cyan-400"
                  title="Kopo — Matching Engine"
                  description="Architected a Bayesian matching engine pairing borrowers and lenders by collateral type, interest rate, and risk profile. Designed atomic writes across the full matching transaction — making double allocation structurally impossible."
                  stack={['Node.js', 'Java (Spring Boot)', 'PostgreSQL', 'Redis']}
                />
                <PortfolioCard
                  tag="Fintech · Cross-border"
                  tagColor="bg-purple-500/10 text-purple-400"
                  title="Standard Focus — OCR Reconciliation"
                  description="Redesigned the OCR ingestion layer for bank transfer reconciliation across India, China, and the Philippines using a Strategy Pattern. A metadata classifier routes receipts to regional processors that return a unified normalised schema."
                  stack={['Node.js', 'PostgreSQL', 'OCR Pipeline']}
                />
                <PortfolioCard
                  tag="IoT · Logistics"
                  tagColor="bg-green-500/10 text-green-400"
                  title="Navisat — Fleet Intelligence"
                  description="Six years building cargo tracking and fleet monitoring across the COMESA region. Built an OTA firmware update framework with dual-partition fail-safe that took deployments from months to hours. Rebuilt the alerts engine into a fully configurable rules engine."
                  stack={['CAN Bus', 'C#', 'Java', 'Scala']}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SKILLS ── */}
      <section className="py-20 md:py-28 px-6 border-t border-gray-800/50">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-10 lg:gap-20">

            <div>
              <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">04</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">Technical Range</p>
              </motion.div>
            </div>

            <div>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-black text-white mb-10"
              >
                Skills & tools
              </motion.h2>

              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.08 } } }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-8"
              >
                {[
                  {
                    category: 'Architecture & Patterns',
                    skills: ['Event-Driven Systems', 'Transactional Outbox', 'Hexagonal Architecture', 'Domain-Driven Design', 'Schema-First API', 'Microservices', 'Saga Pattern', 'Distributed Locking'],
                  },
                  {
                    category: 'Languages',
                    skills: ['Node.js', 'TypeScript', 'Java (Spring Boot)', 'PHP (Zend)', 'Scala', 'C#'],
                  },
                  {
                    category: 'Messaging & Streaming',
                    skills: ['Apache Kafka', 'RabbitMQ', 'BullMQ'],
                  },
                  {
                    category: 'Databases',
                    skills: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis'],
                  },
                  {
                    category: 'Infrastructure & Cloud',
                    skills: ['AWS (EC2, S3, RDS)', 'Docker', 'Jenkins', 'CI/CD'],
                  },
                  {
                    category: 'Observability',
                    skills: ['ELK Stack', 'Structured Logging'],
                  },
                ].map((group) => (
                  <motion.div key={group.category} variants={fadeUp}>
                    <SkillGroup {...group} />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOG ── */}
      <section className="py-20 md:py-28 px-6 border-t border-gray-800/50">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-10 lg:gap-20">

            <div>
              <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">05</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">Engineering Notes</p>
              </motion.div>
            </div>

            <div>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-black text-white mb-8"
              >
                Writing
              </motion.h2>

              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.12 } } }}
                className="grid grid-cols-1 md:grid-cols-3 gap-5"
              >
                {blogPosts.map((post, i) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONNECT ── */}
      <section className="py-20 md:py-28 px-6 border-t border-gray-800/50">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="rounded-3xl border border-gray-800 bg-gray-900 p-10 md:p-16 text-center relative overflow-hidden"
          >
            {/* Subtle top glow */}
            <div
              className="absolute top-0 left-0 w-full h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(249,115,22,0.4), transparent)' }}
            />

            <p className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-6">06 · Connect</p>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
              Let's work together.
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-10 text-base leading-relaxed">
              I'm looking for roles where I can architect high-scale systems that solve real reliability problems —
              distributed infrastructure, integration platforms, or fintech at production volume.
              I thrive in asynchronous-first environments and distributed teams across time zones.
              Based in Nairobi, Kenya. Available for remote roles globally.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <a
                href="mailto:josephmacharia91@gmail.com"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-black text-sm uppercase tracking-wider rounded-full transition-colors"
              >
                <Mail className="w-4 h-4" /> Email me
              </a>
              <a
                href="https://linkedin.com/in/j-macharia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-gray-700 hover:border-orange-500 text-gray-300 hover:text-white font-bold text-sm uppercase tracking-wider rounded-full transition-all"
              >
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
              {/* Temporarily hidden — re-enable when the CV is ready to share again.
              <a
                href="/joseph-macharia-cv.pdf"
                download
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-gray-700 hover:border-orange-500 text-gray-300 hover:text-white font-bold text-sm uppercase tracking-wider rounded-full transition-all"
              >
                <Download className="w-4 h-4" /> Download CV
              </a>
              */}
            </div>

            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
              <MapPin className="w-3.5 h-3.5 text-orange-500/60" />
              <span>Nairobi, Kenya · EAT (UTC+3) · Available for async remote</span>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
